import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"
import { nextFrame } from "../helpers/timing_helpers"

export default class extends Controller {
  static values = {
    url: String,
    itemSelector: { type: String, default: "[data-sortable-id]" }
  }

  connect() {
    this.#bindHandlers()
    this.element.addEventListener("dragstart", this.onDragStart)
    this.element.addEventListener("dragover",  this.onDragOver)
    this.element.addEventListener("dragleave", this.onDragLeave)
    this.element.addEventListener("drop",      this.onDrop)
    this.element.addEventListener("dragend",   this.onDragEnd)
  }

  disconnect() {
    this.element.removeEventListener("dragstart", this.onDragStart)
    this.element.removeEventListener("dragover",  this.onDragOver)
    this.element.removeEventListener("dragleave", this.onDragLeave)
    this.element.removeEventListener("drop",      this.onDrop)
    this.element.removeEventListener("dragend",   this.onDragEnd)
  }

  #bindHandlers() {
    this.onDragStart = this.#handleDragStart.bind(this)
    this.onDragOver  = this.#handleDragOver.bind(this)
    this.onDragLeave = this.#handleDragLeave.bind(this)
    this.onDrop      = this.#handleDrop.bind(this)
    this.onDragEnd   = this.#handleDragEnd.bind(this)
  }

  async #handleDragStart(event) {
    const item = event.target.closest(this.itemSelectorValue)
    if (!item) return
    event.dataTransfer.effectAllowed = "move"
    this.draggedItem = item
    await nextFrame()
    item.classList.add("sortable-dragging")
  }

  #handleDragOver(event) {
    if (!this.draggedItem) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    const target = event.target.closest(this.itemSelectorValue)
    if (!target || target === this.draggedItem) return
    this.#clearDropIndicators()
    const rect = target.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    target.classList.add(event.clientY < midY ? "sortable-drop-before" : "sortable-drop-after")
  }

  #handleDragLeave(event) {
    if (!this.element.contains(event.relatedTarget)) this.#clearDropIndicators()
  }

  #handleDrop(event) {
    if (!this.draggedItem) return
    event.preventDefault()
    const target = event.target.closest(this.itemSelectorValue)
    if (target && target !== this.draggedItem) {
      const rect = target.getBoundingClientRect()
      const midY = rect.top + rect.height / 2
      event.clientY < midY ? target.before(this.draggedItem) : target.after(this.draggedItem)
    }
    this.#clearDropIndicators()
    this.#persistOrder()
  }

  #handleDragEnd() {
    this.draggedItem?.classList.remove("sortable-dragging")
    this.#clearDropIndicators()
    this.draggedItem = null
  }

  #clearDropIndicators() {
    this.element.querySelectorAll(this.itemSelectorValue).forEach(item => {
      item.classList.remove("sortable-drop-before", "sortable-drop-after")
    })
  }

  #persistOrder() {
    const ids = [...this.element.querySelectorAll(this.itemSelectorValue)]
      .map(item => item.dataset.sortableId)
      .filter(Boolean)
    const body = new FormData()
    ids.forEach(id => body.append("ids[]", id))
    patch(this.urlValue, { body })
  }
}
